<template>
  <div class="gf-files-list-item">
    <div
      v-if="file.submodule"
      class="gf-files-list-item__cell --submodule"
      data-cell-type="filename"
    >
      <i class="gf-files-list-item__cell-icon-submodule"></i>
      <a :href="file?.commitRef ? file?.commitRef : file?.projectRef">
        <span class="gf-files-list-item__cell-text">{{ file?.filename }}</span>
        <span>{{ file?.submoduleHead && '@' + file?.submoduleHead?.slice(0, 7) }}</span>
      </a>
    </div>
    <div
      v-else
      class="gf-files-list-item__cell"
      data-cell-type="filename"
    >
      <i
        class="gf-files-list-item__cell-icon"
        :class="fileIcon"
        :style="{ color: fileIconColor }"
      ></i>
      <div style="display: inline-grid">
        <router-link
          v-if="!hasReload"
          class="gf-files-list-item__cell-text text-link"
          tag="span"
          :to="{
            name: file?.isTree ? 'project-files' : 'project-file-blob',
            query: query
          }"
          exact
        >
          <span class="mr-2">{{ file?.filename }}</span>
        </router-link>
        <a
          v-else
          :href="blob"
          class="gf-files-list-item__cell-text text-link text-dark"
        >
          <span class="mr-2">{{ file?.filename }}</span>
        </a>
      </div>

      <!-- LFS and locked logic -->
      <span
        v-if="file?.lfsOid"
        class="d-flex align-items-center border rounded-pill text-muted gf-files-list-item__cell-text__lfs-badge"
      >
        <span class="gf-files-list-item__cell-text__lfs-badge-text">LFS</span>
        <gf-custom-tooltip v-if="file?.lockedBy">
          <template #element>
            <span class="d-flex align-items-center">
              <i class="gf-icon gf-icon-lock text-muted ml-1 gf-fs-1"></i>
            </span>
          </template>
          <template #content>
            <span class="text-truncate">{{ $t('file_locked_by') }}: @{{ file.lockedBy }}</span>
          </template>
        </gf-custom-tooltip>
      </span>
      <gf-custom-tooltip v-else-if="!file?.lfsOid && file?.lockedBy">
        <template #element>
          <span class="d-flex align-items-center">
            <i class="gf-icon gf-icon-lock text-muted gf-fs-1"></i>
          </span>
        </template>
        <template #content>
          <span class="text-truncate">{{ $t('file_locked_by') }}: @{{ file.lockedBy }}</span>
        </template>
      </gf-custom-tooltip>
      <!-- LFS and locked logic -->
    </div>
    <div
      class="gf-files-list-item__cell"
      data-cell-type="commit"
    >
      <a
        class="gf-files-list-item__cell-text"
        :href="'/project/' + alias + '/' + projectAlias + '/commit/' + commit?.hash"
      >
        {{ commit?.shortMessage }}
      </a>
    </div>
    <div
      class="gf-files-list-item__cell"
      data-cell-type="date"
    >
      <span class="gf-files-list-item__cell-text">
        {{ $moment(commit?.authorIdent?.when).fromNow() }}
      </span>
    </div>
  </div>
</template>

<script>
import sharedComponentsMessages from '@/shared-modules/shared-components/sharedComponentsMessages';
import GfCustomTooltip from '@/shared-modules/shared-components/shared/GfCustomTooltip.vue';
export default {
  components: { GfCustomTooltip },
  name: 'GfFilesListItem',
  props: {
    file: {
      type: Object,
      default() {
        return {};
      }
    },
    commit: {
      type: Object,
      default() {
        return {};
      }
    },
    hasReload: {
      type: Boolean,
      default: false
    },
    // eslint-disable-next-line vue/require-default-prop
    branchName: {
      type: String
    }
  },
  computed: {
    branch() {
      return this.$route?.query?.branch || document.getElementById('currentBranch')?.value;
    },
    commitHash() {
      return this.$route?.query?.commit;
    },
    alias() {
      return this.$route?.params?.alias;
    },
    projectAlias() {
      return this.$route?.params?.projectAlias;
    },
    fileIcon() {
      return this.file?.isTree ? ' gf-icon gf-icon-folder' : ' gf-icon gf-icon-file';
    },
    fileIconColor() {
      return this.file?.isTree ? 'var(--gf-folder-c)' : 'var(--text-muted)';
    },
    query() {
      const query = { file: this.file?.path };
      if (this.commitHash) query.commit = this.commitHash;
      else if (this.branch) query.branch = this.branch;
      return query;
    },
    blob() {
      const filename = encodeURIComponent(this.file?.filename);
      const branchName = encodeURIComponent(this.branchName);
      if (this.file?.isTree) {
        return `/project/${this.alias}/${this.projectAlias}/file/?file=${filename}&branch=${branchName}`;
      }
      return `/project/${this.alias}/${this.projectAlias}/blob/?file=${filename}&branch=${branchName}`;
    }
  },
  watch: {
    '$route.path': function () {
      if (this.hasReload) {
        window.location.reload();
      }
    }
  },
  i18n: {
    messages: sharedComponentsMessages
  }
};
</script>

<style lang="scss">
$this: '.gf-files-list-item';

#{$this} {
  padding: 7px;
  display: flex;
  border-top: 1px solid var(--border-color);

  &:first-child {
    border-top: none;
  }

  #{$this}__cell {
    padding: 0 5px;
    display: flex;
    align-items: center;
    overflow: hidden;

    &.--submodule {
      align-items: center;
      overflow: hidden !important;

      & > a {
        width: 100%;
        display: flex;
        overflow: hidden;
      }
    }

    &-text {
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &__lfs-badge {
        padding: 0 7px;

        &-text {
          font-size: 0.75rem;
        }
      }
    }
  }
  #{$this}__cell[data-cell-type='filename'] {
    flex: 0 0 35%;
    overflow: unset;

    @media screen and (max-width: 543px) {
      flex: 0 0 50%;
    }
  }
  #{$this}__cell[data-cell-type='commit'] {
    flex: 0 0 40%;

    @media screen and (max-width: 543px) {
      display: none;
    }

    & > a {
      color: var(--text-muted);
    }
  }
  #{$this}__cell[data-cell-type='date'] {
    flex: 0 0 25%;
    justify-content: flex-end;
    color: var(--text-muted);

    @media screen and (max-width: 543px) {
      flex: 0 0 50%;
    }
  }
  #{$this}__cell-icon {
    display: flex;
    justify-content: flex-end;
    margin-right: 5px;
    pointer-events: none;
  }
  #{$this}__cell-icon-submodule {
    height: 20px;
    width: 20px;
    margin-right: 5px;
    mask-image: url('@/assets/submodule.svg');
    mask-size: 20px 20px;
    background-color: var(--gf-folder-c);
  }
  #{$this}__cell-text.text-link {
    cursor: pointer;

    &:hover {
      color: var(--primary-dark);
    }
  }
}
</style>
